/**
 * RentalPreparationJobSection — "ใบงานเตรียมเครื่อง" ในหน้ารายละเอียดการเช่า
 *
 * รายการเช่าที่เกิดจากการแปลงใบงาน (`convert-to-rental`) จะย้อนกลับไปหาใบงานต้นทางได้จากที่นี่
 * — ให้ Support/บัญชีตามหลังได้ว่าเครื่องชุดนี้มาจากงานที่เซลส่งเข้ามาใบไหน
 *
 * backend: `GET /preparation-jobs/?rental_id=...` (perm `preparation_jobs.view`)
 * — เดิมต้องดึงใบ DELIVERED ทั้งหมดมากรองฝั่ง FE เพราะ backend ยังไม่มี filter นี้
 * ตอนนี้กรองฝั่ง server ได้แล้ว (2026-09-08) จึงไม่ต้องโหลดทั้งชุดมาทิ้ง
 *
 * การ์ดซ่อนตัวเองเมื่อไม่มีสิทธิ์ดู หรือรายการเช่านี้ไม่ได้มาจากใบงาน
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { RiClipboardLine, RiExternalLinkLine, RiErrorWarningLine } from 'react-icons/ri';
import PreparationJobStatusBadge from '../preparation/PreparationJobStatusBadge';
import preparationJobService, {
  PREP_PERMS,
  customerDisplay,
  itemsSummary,
  siteDisplay,
} from '../../services/preparationJobService';
import { usePermission } from '../../hooks/usePermission';
import { formatDate } from '../../utils/dateUtils';

interface RentalPreparationJobSectionProps {
  rentalId: number;
}

const RentalPreparationJobSection: React.FC<RentalPreparationJobSectionProps> = ({ rentalId }) => {
  const { hasPermission } = usePermission();
  const canView = hasPermission(PREP_PERMS.view);

  const { data, isLoading, error } = useQuery({
    queryKey: ['preparation-jobs', 'by-rental', rentalId],
    queryFn: () => preparationJobService.getJobs({ rental_id: rentalId, page_size: 10 }),
    // ไม่มีสิทธิ์ดู = ไม่ยิง API (กัน 403 เงียบ ๆ — pattern G-27)
    enabled: canView && !!rentalId,
    retry: false,
    staleTime: 60 * 1000,
  });

  if (!canView) return null;

  const jobs = data?.items || [];

  // ไม่มีใบงานผูกกับสัญญานี้ → ไม่ต้องมีการ์ดเปล่า ๆ (ส่วนใหญ่ของรายการเช่าเดิมเป็นแบบนี้)
  if (!isLoading && !error && jobs.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
      <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between gap-2">
        <h2 className="text-lg font-medium text-secondary-900 flex items-center">
          <RiClipboardLine className="w-5 h-5 mr-2 text-secondary-500" />
          ใบงานเตรียมเครื่อง
          {jobs.length > 0 && (
            <span className="ml-2 bg-secondary-100 text-secondary-600 text-sm px-2 py-0.5 rounded-full">
              {jobs.length}
            </span>
          )}
        </h2>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-6 text-secondary-500">
            <span className="text-sm">กำลังโหลด...</span>
          </div>
        ) : error ? (
          <div className="flex items-start gap-2 text-sm text-secondary-500">
            <RiErrorWarningLine className="w-4 h-4 text-warning-500 flex-shrink-0 mt-0.5" />
            <span>
              ไม่สามารถโหลดใบงานของสัญญานี้ได้ — ดูรายการทั้งหมดได้ที่{' '}
              <Link to="/rentals/preparation-jobs" className="text-primary-600 hover:underline">
                คิวงานเตรียมเครื่อง
              </Link>
            </span>
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <Link
                key={job.id}
                to={`/rentals/preparation-jobs/${job.id}`}
                className="block border border-secondary-200 rounded-lg p-4 hover:bg-secondary-50 transition-colors"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <span className="font-medium text-secondary-900">{job.job_number}</span>
                  <PreparationJobStatusBadge
                    status={job.status}
                    statusLabelText={job.status_label}
                    priority={job.priority}
                    priorityLabelText={job.priority_label}
                    size="xs"
                  />
                </div>
                <p className="text-sm text-secondary-700">
                  {customerDisplay(job)} · {siteDisplay(job)}
                </p>
                <p className="text-xs text-secondary-500">
                  {itemsSummary(job.items)}
                  {job.needed_date ? ` · ลูกค้าต้องการ ${formatDate(job.needed_date)}` : ''}
                  {job.requested_by_name ? ` · เปิดโดย ${job.requested_by_name}` : ''}
                </p>
              </Link>
            ))}
            <Link
              to="/rentals/preparation-jobs"
              className="inline-flex items-center text-xs text-primary-600 hover:underline"
            >
              ดูคิวงานเตรียมเครื่องทั้งหมด
              <RiExternalLinkLine className="w-3.5 h-3.5 ml-1" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default RentalPreparationJobSection;
